/**
 * Currency Service
 * 
 * API operations for exchange rates and currency conversion.
 * Fetches live exchange rates from the backend (which gets daily rates from exchangerate-api.com)
 */

import { api } from '../utils/api'
import type { Currency } from '../contexts/CurrencyContext'

export interface ExchangeRates {
  USD: number
  AUD: number
  GBP: number
  EUR: number
  JPY: number
  CNY: number
  [key: string]: number
}

export interface ExchangeRatesResponse {
  rates: ExchangeRates
  baseCurrency: string
  lastUpdated: string | null
  currencyCount: number
}

export interface ConvertResponse {
  from: {
    currency: string
    amount: number
  }
  to: {
    currency: string
    amount: number
  }
  rate: number
  timestamp: string
}

// Fallback rates, used ONLY if the API is unreachable AND no fresh cached rate
// exists. These are approximate — the authoritative rates always come from the API.
//
// KEEP IN SYNC with getFallbackRates() in backend/src/services/currency.service.ts.
// The two tables silently drifted apart (1.50 here vs 1.53 there — a flat 2% on
// every AUD price), and while both sides still converted independently that gap
// landed straight on the difference between what this app showed and what the
// storefront charged. Listing prices now come pre-resolved from the server, so
// this table no longer decides them; it still backs display-currency switching,
// which is reason enough for the two to agree.
const FALLBACK_RATES: ExchangeRates = {
  USD: 1.0,
  AUD: 1.50,
  GBP: 0.76,
  EUR: 0.88,
  JPY: 155.00,
  CNY: 7.15
}

// A cached rate older than this is treated as untrustworthy for seeding the
// initial render — better to fall back and let the live fetch correct it than
// to price against a days-old rate.
const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000

// Cache for rates (persisted in memory with timestamp)
let cachedRates: ExchangeRates | null = null
let cacheTimestamp: number | null = null
const CACHE_DURATION_MS = 60 * 60 * 1000 // 1 hour (backend updates daily)

/**
 * Currency API service
 */
export const currencyService = {
  /**
   * Get current exchange rates
   * Uses in-memory cache with 1-hour TTL to minimize API calls
   */
  async getRates(forceRefresh = false): Promise<ExchangeRates> {
    // Return cached rates if valid
    if (!forceRefresh && cachedRates && cacheTimestamp) {
      const cacheAge = Date.now() - cacheTimestamp
      if (cacheAge < CACHE_DURATION_MS) {
        return cachedRates
      }
    }

    try {
      const response = await api.get<{ success: boolean; data: ExchangeRatesResponse }>('/currency/rates')
      
      if (response.data.success && response.data.data?.rates) {
        const fetchedRates = response.data.data.rates
        cachedRates = fetchedRates
        cacheTimestamp = Date.now()
        
        // Also persist to localStorage for faster initial load
        try {
          localStorage.setItem('exchangeRates', JSON.stringify({
            rates: fetchedRates,
            timestamp: cacheTimestamp
          }))
        } catch {
          // localStorage might be unavailable
        }
        
        return fetchedRates
      }
      
      throw new Error('Invalid response from exchange rates API')
    } catch (error) {
      console.warn('Failed to fetch exchange rates, using cached/fallback rates:', error)
      
      // Try localStorage cache
      try {
        const stored = localStorage.getItem('exchangeRates')
        if (stored) {
          const { rates, timestamp } = JSON.parse(stored)
          // Accept localStorage cache up to 24 hours old
          if (Date.now() - timestamp < 24 * 60 * 60 * 1000 && rates) {
            cachedRates = rates as ExchangeRates
            cacheTimestamp = timestamp
            return cachedRates
          }
        }
      } catch {
        // localStorage parse failed
      }
      
      // Return fallback rates
      return FALLBACK_RATES
    }
  },

  /**
   * Get rate for a specific currency (relative to USD)
   */
  async getRate(currency: Currency): Promise<number> {
    const rates = await this.getRates()
    return rates[currency] ?? 1.0
  },

  /**
   * Convert amount between currencies using server-side rates
   * @param amount - Amount in source currency (dollars, not cents)
   * @param from - Source currency
   * @param to - Target currency
   */
  async convert(amount: number, from: Currency, to: Currency): Promise<ConvertResponse> {
    const response = await api.get<{ success: boolean; data: ConvertResponse }>('/currency/convert', {
      params: { amount, from, to }
    })
    
    if (response.data.success && response.data.data) {
      return response.data.data
    }
    
    throw new Error('Failed to convert currency')
  },

  /**
   * Client-side conversion using cached rates (faster, no API call)
   * @param cents - Amount in cents
   * @param from - Source currency
   * @param to - Target currency
   * @returns Amount in cents in target currency
   */
  convertLocal(cents: number, from: Currency, to: Currency, rates: ExchangeRates): number {
    if (from === to) return cents
    
    // Convert from source to USD (base)
    const centsInUSD = cents / (rates[from] ?? 1)
    
    // Convert from USD to target
    const centsInTarget = centsInUSD * (rates[to] ?? 1)
    
    return Math.round(centsInTarget)
  },

  /**
   * Clear the in-memory cache
   */
  clearCache(): void {
    cachedRates = null
    cacheTimestamp = null
    try {
      localStorage.removeItem('exchangeRates')
    } catch {
      // Ignore localStorage errors
    }
  },

  /**
   * Get fallback rates (for initialization before API response).
   * Only seeds from localStorage when the cached rate is recent; a stale cache
   * (e.g. a rate from several days ago) would otherwise silently misprice the
   * initial render until the live fetch lands.
   */
  getFallbackRates(): ExchangeRates {
    try {
      const stored = localStorage.getItem('exchangeRates')
      if (stored) {
        const { rates, timestamp } = JSON.parse(stored)
        if (rates && typeof timestamp === 'number' && Date.now() - timestamp < MAX_CACHE_AGE_MS) {
          return rates
        }
      }
    } catch {
      // Ignore
    }
    return FALLBACK_RATES
  }
}
